import { z } from "zod";
import { MinorUnitsSchema, ShopIdSchema, ShopPhotoSchema } from "../schema";
const PaymentImageSchema = z
    .object({
    assetId: ShopIdSchema,
    version: z.number().int().positive(),
    objectKey: z
        .string()
        .regex(/^assets\/uploads\/[a-f0-9]{64}\.(png|jpg|webp)$/),
    mimeType: z.enum(["image/png", "image/jpeg", "image/webp"]),
    sha256: z.string().regex(/^[a-f0-9]{64}$/),
    width: z.number().int().positive().optional(),
    height: z.number().int().positive().optional(),
})
    .strict();
export const ManualMethodSchema = z
    .object({
    id: ShopIdSchema.refine((id) => !["stripe", "free"].includes(id), "This payment method couldn't be saved. Add it again."),
    label: z.string().trim().min(1).max(100),
    instructions: z.string().trim().min(1).max(10000),
    image: PaymentImageSchema.nullable(),
    pickupOnly: z.boolean(),
})
    .strict();
export const ShippingRegionSchema = z
    .object({
    id: ShopIdSchema,
    name: z.string().trim().min(1).max(100),
    countries: z
        .array(z
        .object({
        code: z.string().regex(/^[A-Z]{2}$/),
        states: z.array(z.string().trim().max(100)).transform((states) => {
            const seen = new Set();
            return states.filter((state) => {
                const key = state.toLowerCase();
                if (!key || seen.has(key))
                    return false;
                seen.add(key);
                return true;
            });
        }),
    })
        .strict())
        .min(1),
    rates: z
        .array(z
        .object({
        id: ShopIdSchema,
        name: z.string().trim().min(1).max(100),
        amount: MinorUnitsSchema,
    })
        .strict())
        .min(1),
    freeOver: MinorUnitsSchema.nullable(),
})
    .strict();
export const CommerceSettingsSchema = z
    .object({
    manualMethods: z.array(ManualMethodSchema),
    emailFooter: z.string().trim().max(2000).optional(),
    emailLogo: PaymentImageSchema.nullable().optional(),
    regions: z.array(ShippingRegionSchema),
    pickup: z
        .object({
        address: z.string().trim().min(1).max(2000),
        instructions: z.string().max(5000),
    })
        .strict()
        .nullable(),
})
    .strict()
    .superRefine((s, ctx) => {
    for (const ids of [
        s.manualMethods.map((m) => m.id),
        s.regions.map((r) => r.id),
        s.regions.flatMap((r) => r.rates.map((x) => x.id)),
    ]) {
        if (new Set(ids).size !== ids.length)
            ctx.addIssue({
                code: "custom",
                message: "Some settings were added twice. Remove the duplicate and try again.",
            });
    }
});
export const emptyCommerce = () => ({
    manualMethods: [],
    regions: [],
    pickup: null,
});
export const BasketLineSchema = z
    .object({
    productId: ShopIdSchema,
    variantId: ShopIdSchema,
    quantity: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER),
    choices: z.record(z.array(ShopIdSchema)).default({}),
    text: z.record(z.string().max(10000)).default({}),
    previousPrice: MinorUnitsSchema.optional(),
})
    .strict();
export const BasketSchema = z.array(BasketLineSchema).max(100);
export const DeliverySchema = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("downloads") }).strict(),
    z.object({ kind: z.literal("pickup") }).strict(),
    z
        .object({
        kind: z.literal("delivery"),
        regionId: ShopIdSchema,
        rateId: ShopIdSchema,
        country: z.string().regex(/^[A-Z]{2}$/),
        state: z.string().trim().max(100),
    })
        .strict(),
]);
export const ContactSchema = z
    .object({
    email: z
        .string()
        .trim()
        .email()
        .max(254)
        .transform((v) => v.toLowerCase()),
    name: z.string().trim().max(160).default(""),
    phone: z.string().trim().max(80).default(""),
    address: z
        .object({
        line1: z.string().trim().min(1).max(200),
        line2: z.string().trim().max(200).default(""),
        city: z.string().trim().min(1).max(100),
        postalCode: z.string().trim().min(1).max(40),
    })
        .strict()
        .nullable()
        .default(null),
    note: z.string().trim().max(5000).default(""),
})
    .strict();
export const StartCheckoutSchema = z
    .object({
    requestId: z.string().uuid(),
    basket: BasketSchema,
    delivery: DeliverySchema,
    contact: ContactSchema,
    payment: z.string().max(80),
})
    .strict();
export class CheckoutRefusal extends Error {
    status;
    name = "CheckoutRefusal";
    constructor(message, status = 409) {
        super(message);
        this.status = status;
    }
}
export const CommerceStateSchema = z.object({
    settings: CommerceSettingsSchema,
    rowVersion: z.number().int().nonnegative(),
    account: z
        .object({ id: z.string(), country: z.string(), ready: z.boolean() })
        .nullable(),
    currencyLocked: z.boolean(),
});
